/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractControlValueAccessorGroup, SharedComponentsModule } from '@irian-ro/shared-components-module';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService, SelectItem } from 'primeng/api';
import { translationSchema } from '@well/translation';
import { AddressDTO, AddressType, DelAddressDtoModel } from '@well/address-management-domain-model';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LegacyChipsComponent } from '@well/infra/legacy-primeng-components';
import {
  CountryDropdownComponent,
  CountyDropdownComponent,
  SectorDropdownComponent,
} from '@well/common-libs/country-management/ui';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { FormUtils } from '@irian-ro/util-forms';
import { AddressTemplateService } from './address-template.service';
import { FloatLabel } from 'primeng/floatlabel';
import { RomanianAddressEnum } from './romanian-address.enum';
import { FormInlineErrorDirective } from '@irian-ro/shared-ui';
import { MapsService } from '@irian-ro/util-maps';
import { AddressSearchResultMapper } from '../g-map-result-mapper/address-search-result.mapper';
import { InputTrimDirective } from '@well/infra/directives';
import { Select, SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { InputTextModule } from 'primeng/inputtext';
@Component({
  selector: 'well-address-inputs',
  templateUrl: './address-inputs.component.html',
  styleUrls: ['./address-inputs.component.css'],
  providers: [
    AddressTemplateService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressInputsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressInputsComponent),
      multi: true,
    }
  ],
  standalone: true,
  imports: [InputTextModule, TextareaModule, 
    FloatLabel,
    CommonModule,
    TranslateModule,
    LegacyChipsComponent,
    ReactiveFormsModule,
    CountryDropdownComponent,
    SharedComponentsModule,
    SelectModule,
    ToggleSwitch,
    FormsModule,
    TooltipModule,
    ButtonModule,
    DialogModule,
    CountyDropdownComponent,
    FormInlineErrorDirective,
    SectorDropdownComponent,
    InputTrimDirective,
    Select,
    ToggleSwitch
  ],
})
export class AddressInputsComponent extends AbstractControlValueAccessorGroup implements OnInit {
  readonly form = (() => {
    const form = inject(FormBuilder).group<AddressDTO>(new AddressDTO());
    form?.get('city')?.addValidators([Validators.required]);
    form?.get('postalCode')?.addValidators([Validators.required]);
    form?.get('country')?.addValidators(!this.eTransportContext ? [Validators.required] : []);
    return form;
  })();

  hide = false;

  get displayTypeField(): boolean {
    return this._displayTypeField;
  }

  @Input() set displayTypeField(value: boolean) {
    this._displayTypeField = value;
    if (value) {
      this.addressTypeValueControl.enable();
      this.addressTypeValueControl.setValidators(Validators.required);
    } else {
      this.addressTypeValueControl.setValidators([]);
    }
  }

  @Input() visibilityTypes: SelectItem[] | null = [];

  @Input() addressTypes: SelectItem[] | null = [];

  sectors = this.addressTemplateService.getSectors();

  private _displayTypeField = true;

  @Input() displayMainAddress = true;

  private _displayVisibilityField = false;

  get displayVisibilityField(): boolean {
    return this._displayVisibilityField;
  }

  @Input() set displayVisibilityField(value: boolean) {
    this._displayVisibilityField = value;
    if (value) {
      this.visibilityTypeControl.enable();
      this.visibilityTypeControl.setValidators(Validators.required);
    } else {
      this.visibilityTypeControl.setValidators([]);
    }
  }

  @Input() shouldAddressBeSaved = true;

  @Input() displayAddressTemplateSave = false;

  get disableFieldsUntilInput(): boolean {
    return this._disableFieldsUntilInput;
  }

  @Input() set disableFieldsUntilInput(value: boolean) {
    this._disableFieldsUntilInput = value;
    this.setDisabledState(value);
  }

  private _disableFieldsUntilInput = true;

  @Input() hideFieldsUntilInput = false;

  @Input() handoverAddress = false;
  @Input() companyRequired = false;
  @Input() showStreetRequiredStar = false;

  @Input() showAddressLabel = true;

  displaySavePopup = false;

  @Input() mainAddress = false;
  @Output() mainAddressChange = new EventEmitter<boolean>();

  @Output() addressTemplateSaved = new EventEmitter<void>();

  @Input() eTransportContext = false;
  @Input() displayAdditionalFields = false;

  destroyRef = inject(DestroyRef);

  @ViewChild('dropdown') addressTypeDropdown: Select | undefined = undefined;

  constructor(
    public addressTemplateService: AddressTemplateService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {
    super();
  }

  private service: google.maps.places.AutocompleteService | undefined = undefined;
  private sessionToken: google.maps.places.AutocompleteSessionToken | undefined = undefined;
  private placeService: google.maps.places.PlacesService | undefined = undefined;

  ngOnInit() {
    this.handleFormValueChanges();
    this.handleETransportContext();
    if (this.eTransportContext) {
      this.map.subscribe((loaded) => {
        if (loaded) {
          this.service = new google.maps.places.AutocompleteService();
          this.sessionToken = new google.maps.places.AutocompleteSessionToken();
          const div = document.createElement('div');
          this.placeService = new google.maps.places.PlacesService(div);
        }
      });
    }
  }

  writeValue(address: AddressDTO): void {
    if (address && this.anyFieldWithDate(address)) {
      if (address.addressLabel && this.handoverAddress) {
        address.addressLabel = null;
        this.resetAddressLabelFormControl();
      }
      if (this.eTransportContext) {
        address.country = RomanianAddressEnum.RO;
      }
      this.showCountyDropdown = address.country === RomanianAddressEnum.RO;
      this.showSectorDropdown = this.isBucharestAddress(address);

      if (this.isBucharestAddress(address) && !this.sectors.includes(address.city)) {
        address.city = null;
      }

      this.form?.patchValue(address, { emitEvent: false });
      this.form?.updateValueAndValidity();

      if (this._disableFieldsUntilInput) {
        this.setDisabledState(false);
      }

      if (this.hideFieldsUntilInput) {
        this.hide = false;
      }

      if (address.addressTypeValue === AddressType.Main) {
        this.mainAddress = true;
      }
    } else {
      this.form?.reset(new AddressDTO(), { emitEvent: false });
      if (this._disableFieldsUntilInput) {
        this.setDisabledState(true);
      }
      if (this.hideFieldsUntilInput) {
        this.hide = true;
      }
    }
    this.handleETransportContext();
  }

  handleFormValueChanges() {
    this.form?.valueChanges?.subscribe((value) => {
      if (this.eTransportContext) {
        value.country = RomanianAddressEnum.RO;
      }
      this.showCountyDropdown = value?.country === RomanianAddressEnum.RO;
      this.showSectorDropdown = this.isBucharestAddress(value as AddressDTO);

      if (this.isBucharestAddress(value as AddressDTO) && !this.sectors.includes(value?.city)) {
        this.form?.get('city')?.patchValue(null, { emitEvent: false });
      }

      if (value?.country === RomanianAddressEnum.RO) {
        this.form?.get('state')?.setValidators(Validators.required);
        this.form?.updateValueAndValidity({ emitEvent: false });
      } else {
        this.form?.get('state')?.setValidators([]);
        this.form?.updateValueAndValidity({ emitEvent: false });
      }

      if (
        (!this.handoverAddress && this.companyRequired) ||
        value?.addressTypeValue === 'Office' ||
        value?.addressTypeValue === 'FiscalResidence'
      ) {
        this.showStreetRequiredStar = true;
        this.setCompanyRequiredFields();
      } else {
        this.showStreetRequiredStar = false;
        this.unsetCompanyRequiredFields();
      }
    });
  }

  protected createForm() {
    // this.form = AddressUtilForm.createFormAddressHandover();
  }

  get addressLabelFormControl() {
    return this.form?.controls['addressLabel'] as FormControl;
  }

  get postalCodeFormControl() {
    return this.form?.controls['postalCode'] as FormControl;
  }

  get addressTypeValueControl() {
    return this.form?.controls['addressTypeValue'] as FormControl;
  }

  get visibilityTypeControl() {
    return this.form?.controls['visibilityType'] as FormControl;
  }

  get streetNameFormControl() {
    return this.form?.controls['streetName'] as FormControl;
  }

  get streetNumberFormControl() {
    return this.form?.controls['streetNumber'] as FormControl;
  }

  setCompanyRequiredFields() {
    this.streetNameFormControl.setValidators([Validators.required]);
    this.streetNumberFormControl.setValidators([Validators.required]);
    this.streetNameFormControl.updateValueAndValidity({ emitEvent: false });
    this.streetNumberFormControl.updateValueAndValidity({ emitEvent: false });
  }

  unsetCompanyRequiredFields() {
    this.streetNameFormControl.setValidators([]);
    this.streetNumberFormControl.setValidators([]);
    this.streetNameFormControl.updateValueAndValidity({ emitEvent: false });
    this.streetNumberFormControl.updateValueAndValidity({ emitEvent: false });
  }

  mainAddressCheckboxChanged(value) {
    if (this.addressTypeDropdown) {
      this.addressTypeDropdown.hide();
    }
    if (value) {
      this.addressTypeValueControl.patchValue(AddressType.Main);
    } else {
      this.addressTypeValueControl.reset();
    }
    this.mainAddress = value;
    this.mainAddressChange.emit(this.mainAddress);
  }

  showAddressLabelDialog() {
    if (this.form?.valid) {
      this.displaySavePopup = true;
      this.addressLabelFormControl.setValidators(Validators.required);
      this.addressLabelFormControl.updateValueAndValidity();
    } else {
      this.resetAddressLabelFormControl();
    }
  }

  hideAddressTemplateSaveDialog() {
    this.displaySavePopup = false;
  }

  map = inject(MapsService).load();
  cdRef = inject(ChangeDetectorRef);

  getCoordinatesForAddress(address: string) {
    this.service
      ?.getPlacePredictions({
        input: address,
        sessionToken: this.sessionToken,
      })
      .then((result) => {
        if (result.predictions.length === 0) {
          this.messageService.add({
            key: 'global-notification',
            severity: 'warn',
            summary: this.translateService.instant('address.inaccurateAddressMessage'),
            detail: '',
            life: 20000,
          });
        } else {
          this.placeService?.getDetails(
            {
              placeId: result.predictions[0].place_id,
              sessionToken: this.sessionToken,
            },
            (placeResult: google.maps.places.PlaceResult) => {
              if (placeResult.geometry === undefined || placeResult.geometry === null) {
                return;
              }

              const resultMapper: AddressSearchResultMapper = new AddressSearchResultMapper(placeResult);
              const result = resultMapper.getAddress();
              this.form?.controls?.pointDTO.patchValue(result.pointDTO);
              this.cdRef.detectChanges();
              this.handleValidAddressTemplateSave();
            }
          );
        }
      });
  }

  onAddressTemplateSave() {
    FormUtils.markAllAsDirty(this.form);
    FormUtils.markAllAsTouched(this.form);
    if (this.form?.valid) {
      if (this.eTransportContext && !this.form?.value?.pointDTO) {
        this.getCoordinatesForAddress(
          this.form?.value?.country +
            this.form?.value?.state +
            this.form?.value?.city +
            this.form?.value?.streetName +
            this.form?.value?.streetNumber
        );
      } else {
        this.handleValidAddressTemplateSave();
      }
    }
  }

  handleValidAddressTemplateSave() {
    this.clearAddressLabelFormControlValidators();
    this.hideAddressTemplateSaveDialog();
    this.addressTemplateService.saveAddressTemplate(this.form?.getRawValue()).subscribe({
      next: (res) => {
        const savedAddress: DelAddressDtoModel = {
          ...res.data,
          basedOnAddressTemplateUuid: res.data?.uuid,
          uuid: this.form?.getRawValue()?.uuid,
        };
        this.form?.patchValue(savedAddress);
        this.cdRef.detectChanges();
        this.addressTemplateSaved.emit();
        this.messageService.add({
          key: 'global-notification',
          severity: 'info',
          summary: this.translateService.instant('address.savedAddressMessage'),
          detail: '',
          life: 5000,
        });
        this.addressLabelFormControl.reset(null, { emitEvent: false });
      },
      error: () => {
        this.addressLabelFormControl.reset(null, { emitEvent: false });
      },
    });
  }

  onHideDialog() {
    this.resetAddressLabelFormControl();
    this.hideAddressTemplateSaveDialog();
  }

  private resetAddressLabelFormControl() {
    this.addressLabelFormControl.reset();
    this.clearAddressLabelFormControlValidators();
  }

  private clearAddressLabelFormControlValidators() {
    this.addressLabelFormControl.clearValidators();
    this.addressLabelFormControl.updateValueAndValidity();
  }

  private anyFieldWithDate(obj: any) {
    return obj.addressLine || obj.city || obj.postalCode || obj.country;
  }

  protected onChangeCallback(res: any): any {
    return super.onChangeCallback(res);
  }

  isBucharestAddress(address: AddressDTO, additionalCondition = true) {
    return this.addressTemplateService.isBucharestAddress(address, additionalCondition);
  }

  handleETransportContext() {
    if (this.eTransportContext) {
      this.form?.get('country')?.clearValidators();
      this.form?.get('country')?.disable();
    }
  }

  protected readonly translationSchema = translationSchema;
  showSectorDropdown = false;
  showCountyDropdown = false;
}
